import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiCode, FiSmartphone, FiBriefcase, FiMonitor, FiShoppingCart, FiSettings } from 'react-icons/fi';
import { UseToast } from '../../hooks/UseToast';
import Button from '../../components/ui/Button';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const ServicesList = () => {
  const navigate = useNavigate();
  const toast = UseToast();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/services`);
      const data = await res.json();
      
      if (data.success) {
        setServices(data.data || []);
      } else {
        toast.error('Failed to load services');
        setServices([]);
      }
    } catch (error) {
      toast.error('Server error');
      console.error('Fetch error:', error);
      setServices([]);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/services/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Service deleted successfully');
        setServices(services.filter(s => s._id!== id));
      } else {
        toast.error(data.message || 'Delete failed');
      }
    } catch (error) {
      toast.error('Something went wrong');
      console.error('Delete error:', error);
    }
  };

  const getIcon = (iconName) => {
    const icons = { 
      FiCode: <FiCode />, 
      FiSmartphone: <FiSmartphone />, 
      FiBriefcase: <FiBriefcase />,
      FiMonitor: <FiMonitor />,
      FiShoppingCart: <FiShoppingCart />,
      FiSettings: <FiSettings />,
      code: <FiCode />,
      smartphone: <FiSmartphone />,
      briefcase: <FiBriefcase />
    };
    return icons[iconName] || <FiCode />;
  };

  return (
    <>
      <style>{`
    .services-page { font-family: 'Outfit', sans-serif; }
    .page-header { margin-bottom: 30px; display: flex; align-items: center; justify-content: space-between; }
    .page-title { font-size: 32px; font-weight: 800; color: #0F172A; margin: 0; }
    .services-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 24px; }
    .service-card { background: #FFF; border-radius: 16px; padding: 28px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55); border: 2px solid transparent; }
    .service-card:hover { transform: translateY(-8px); box-shadow: 0 12px 40px rgba(59, 130, 246, 0.15); border-color: #3B82F6; }
    .service-icon { width: 56px; height: 56px; border-radius: 14px; background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%); display: flex; align-items: center; justify-content: center; color: #FFF; font-size: 28px; margin-bottom: 16px; }
    .service-name { font-size: 20px; font-weight: 700; color: #0F172A; margin: 0 0 8px 0; }
    .service-desc { font-size: 14px; color: #64748B; line-height: 1.6; margin-bottom: 12px; }
    .service-features { margin-bottom: 20px; }
    .feature-tag { display: inline-block; padding: 4px 10px; background: #F1F5F9; border-radius: 6px; font-size: 12px; color: #475569; margin: 4px 4px 0 0; }
    .service-actions { display: flex; gap: 8px; }
    .action-btn { flex: 1; padding: 10px; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; transition: all 0.3s ease; }
    .btn-edit { background: #DBEAFE; color: #3B82F6; }
    .btn-edit:hover { background: #3B82F6; color: #FFF; }
    .btn-delete { background: #FEE2E2; color: #EF4444; }
    .btn-delete:hover { background: #EF4444; color: #FFF; }
    .loading-state,.empty-state { text-align: center; padding: 60px 20px; color: #64748B; }
    .empty-icon { font-size: 64px; margin-bottom: 16px; opacity: 0.5; }
    .status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 600; margin-bottom: 12px; }
    .status-active { background: #D1FAE5; color: #065F46; }
    .status-inactive { background: #F3F4F6; color: #6B7280; }
      `}</style>
      <div className="services-page">
        <div className="page-header">
          <h1 className="page-title">Services</h1>
          <Button onClick={() => navigate('/services/add')} icon={<FiPlus />}>
            Add Service
          </Button>
        </div>

        {loading? (
          <div className="loading-state">Loading services...</div>
        ) : services.length === 0? (
          <div className="empty-state">
            <div className="empty-icon">💼</div>
            <p style={{ fontSize: '16px', fontWeight: '600', color: '#0F172A' }}>No services yet</p>
            <p style={{ fontSize: '14px', color: '#64748B' }}>Create your first service to get started</p>
          </div>
        ) : (
          <div className="services-grid">
            {services.map((service) => (
              <div key={service._id} className="service-card">
                <div className="service-icon">{getIcon(service.icon)}</div>
                <span className={`status-badge ${service.isActive? 'status-active' : 'status-inactive'}`}>
                  {service.isActive? 'Active' : 'Inactive'}
                </span>
                <h3 className="service-name">{service.title}</h3>
                <p className="service-desc">{service.description}</p>
                {service.features && service.features.length > 0 && (
                  <div className="service-features">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <span key={idx} className="feature-tag">{feature}</span>
                    ))}
                    {service.features.length > 3 && (
                      <span className="feature-tag">+{service.features.length - 3} more</span>
                    )}
                  </div>
                )}
                <div className="service-actions">
                  <button 
                    className="action-btn btn-edit" 
                    onClick={() => navigate(`/services/edit/${service._id}`)}
                  >
                    <FiEdit2 /> Edit
                  </button>
                  <button 
                    className="action-btn btn-delete" 
                    onClick={() => handleDelete(service._id)}
                  >
                    <FiTrash2 /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default ServicesList;